import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { BackLink } from "../../ui/links";
import { Notice } from "../../ui/notice";
import { PageHeader } from "../../ui/page-header";
import { useCatalogCapabilities } from "../access";
import { hintText } from "../components/styles";
import { describeCatalogError } from "../errors";
import {
  isSearchRequested,
  searchBodyFromValues,
  searchParamsFromValues,
  searchValuesFromParams,
  validateSearchValues,
} from "./form";
import { useCatalogSearch, useDietaryTags } from "./queries";
import { SearchForm } from "./SearchForm";
import { SearchResults } from "./SearchResults";

/**
 * Catalog search for staff (CHT-710). A staff member runs a search and reads the scores and the reasons
 * behind each hit. The page only reads: it has no button that writes. The search lives in the address
 * (`?q=jazz&type=EVENT&go=1`), so a link shares it. The API decides access again on every request.
 */
export function SearchPage() {
  useWideLayout();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { capabilities, isLoading: rolesLoading } = useCatalogCapabilities();
  const parsed = useMemo(() => searchValuesFromParams(params), [params]);
  const requested = isSearchRequested(params);
  const urlErrors = useMemo(
    () => (requested ? validateSearchValues(parsed.values) : {}),
    [requested, parsed.values],
  );
  const body = useMemo(
    () =>
      requested && Object.keys(urlErrors).length === 0 ? searchBodyFromValues(parsed.values) : null,
    [requested, urlErrors, parsed.values],
  );

  const search = useCatalogSearch(capabilities.read ? body : null);
  const dietary = useDietaryTags(capabilities.read);
  const dietaryChoices = dietary.isPending
    ? null
    : dietary.isError
      ? []
      : dietary.data.items.map((tag) => ({ code: tag.code, label: tag.label }));

  // After a search the focus goes to the results heading, so a keyboard or screen reader user lands on
  // the answer. `wantFocus` is set by a submit and cleared once the heading has the focus.
  const headingRef = useRef<HTMLHeadingElement>(null);
  const wantFocus = useRef(false);
  const settled = body !== null && !search.isFetching && (search.isSuccess || search.isError);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the update times mark a new answer
  useEffect(() => {
    if (settled && wantFocus.current) {
      wantFocus.current = false;
      headingRef.current?.focus();
    }
  }, [settled, search.dataUpdatedAt, search.errorUpdatedAt]);

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.read) {
    return (
      <AsyncState
        kind="error"
        title="The catalog is not available for your role"
        message="Your staff role does not include catalog access."
      />
    );
  }

  const submit = (values: Parameters<typeof searchParamsFromValues>[0]) => {
    wantFocus.current = true;
    const next = searchParamsFromValues(values);
    if (next.toString() === params.toString()) void search.refetch();
    else setParams(next);
  };

  return (
    <div className="grid min-w-0 gap-6">
      <PageHeader
        title="Catalog search"
        description="Run a search and read the scores and the reasons behind each result. This page only reads. It changes no data."
        breadcrumbs={<BackLink to="/catalog">Back to the catalog</BackLink>}
      />

      {parsed.ignored.length > 0 ? (
        <Notice tone="warning" role="status">
          Some values in the address were not valid and were ignored: {parsed.ignored.join(", ")}.
        </Notice>
      ) : null}

      <SearchForm
        key={params.toString()}
        initial={parsed.values}
        initialErrors={urlErrors}
        busy={search.isFetching}
        dietaryChoices={dietaryChoices}
        onSubmit={submit}
        onClear={() => navigate("/catalog/search")}
      />

      <section className="grid min-w-0 gap-3" aria-labelledby="search-results-title">
        <h2
          id="search-results-title"
          ref={headingRef}
          tabIndex={-1}
          className="m-0 font-serif text-[26px] leading-8 font-medium"
        >
          Results
        </h2>
        <div aria-busy={search.isFetching} className="grid min-w-0 gap-3">
          {!requested ? (
            <p className={hintText}>Fill in the form and choose Search. The results appear here.</p>
          ) : body === null ? (
            <Notice tone="warning" role="alert">
              The values in the form are not valid, so no search ran. Fix the fields marked above.
            </Notice>
          ) : search.isFetching ? (
            <AsyncState kind="loading" title="Searching" message="Waiting for the answer." />
          ) : search.isError ? (
            <AsyncState
              kind="error"
              title="The search failed"
              message={describeCatalogError(search.error).message}
              onRetry={() => {
                wantFocus.current = true;
                void search.refetch();
              }}
            />
          ) : search.data === undefined ? null : (
            <SearchResults result={search.data} />
          )}
        </div>
      </section>
    </div>
  );
}
